// GapEntry — Sprint 19BH Day 1 Step 1.3 · single audit finding record.
//
// Why this exists (v4-rewrite.md §13.1):
//   Audit runs over the entire IngestPackage post-pipeline (Phase 4 step.do)
//   and emits one GapEntry per violation. Each entry carries:
//     - the invariant kind that was violated
//     - severity (info / warn / error)
//     - locus (stage + optional case/topic/concept/itemIds for drill-down)
//     - human-readable message
//     - OPTIONAL `suggestedAction`: a RegenAction the user can one-click
//       in the regen UI to fix the gap.
//
// Why direct RegenAction import (not z.lazy):
//   Plan §6 risk row notes z.lazy forward refs complicate type inference. We
//   land Step 1.4 (regen-action.ts) BEFORE Step 1.3 (this file) to break the
//   cycle — RegenActionSchema is fully defined at this file's parse time,
//   so a direct import is the simplest and most strictly-typed solution.
import { z } from 'zod';
import { StageIdSchema } from './stage-id.js';
import { OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE } from './curriculum-outline.js';
import { AUDIT_INVARIANT_KINDS, } from './audit-invariant.js';
import { RegenActionSchema } from './regen-action.js';
/** Severity tier. info = surfaced but no action needed; warn = consider; error = must address. */
export const GapSeveritySchema = z.enum(['info', 'warn', 'error']);
/**
 * One audit finding. The id is a ulid so the regen UI can deep-link
 * (`POST /ingest/regenerate` with `fill_audit_gap` action carrying this id).
 */
export const GapEntrySchema = z.object({
    /** ULID — stable across audit runs of the same package + same invariant. */
    id: z.string().min(1).max(64)
        .describe('ULID. Stable across re-audits of the same package; referenced by RegenAction `fill_audit_gap`.'),
    /** Which AuditInvariant kind caught this gap (exhaustive enum from audit-invariant.ts). */
    invariantKind: z.enum(AUDIT_INVARIANT_KINDS)
        .describe('Discriminator into the AuditInvariant union that produced this gap.'),
    /** Severity: info < warn < error. */
    severity: GapSeveritySchema
        .describe('Severity tier. Drives badge color in the regen UI.'),
    /** Stage that produced (or failed to produce) the item(s) in question. */
    stage: StageIdSchema
        .describe('Stage id where the gap originates. Used by regen workflow to pick context.'),
    /** Optional drill-down loci. Outline ids when present. */
    caseId: z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE).optional()
        .describe('Optional chapter id locus.'),
    topicId: z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE).optional()
        .describe('Optional topic id locus.'),
    conceptId: z.string().regex(OUTLINE_ID_PATTERN, OUTLINE_ID_MESSAGE).optional()
        .describe('Optional concept id locus.'),
    /** Specific item ids implicated (for dedup / format gaps). */
    itemIds: z.array(z.string().min(1).max(120)).optional()
        .describe('Item ids implicated in the gap (e.g. the two near-duplicate items).'),
    /** Human-readable message. Surfaced in the audit UI exactly. */
    message: z.string().min(1).max(500)
        .describe('Plain-language explanation. 1–500 chars. Shown verbatim to the user.'),
    /** Optional regen action that fixes this gap. UI offers a one-click button labelled by action kind. */
    suggestedAction: RegenActionSchema.optional()
        .describe('Optional RegenAction the user can one-click to fix this gap.'),
}).passthrough();
/** Sanity helper used by audit checkers + tests. */
export function isHighSeverity(entry) {
    return entry.severity === 'error';
}
//# sourceMappingURL=audit-gap-entry.js.map